import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { RelatoriosService, fmtMoeda } from '../services';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function Relatorios() {
  const [tab, setTab] = useState('mensal');
  const [mes, setMes] = useState(new Date().toISOString().substring(0, 7));
  const [ano, setAno] = useState(new Date().getFullYear());
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setDados(null);

    const carregar = tab === 'mensal'
      ? RelatoriosService.mensal(mes)
      : tab === 'anual'
        ? RelatoriosService.anual(ano)
        : RelatoriosService.patrimonio();

    carregar
      .then(d => { if (!cancelled) setDados(d); })
      .catch(() => { if (!cancelled) setDados(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [tab, mes, ano]);

  const renderMensal = () => {
    if (!dados) return null;
    const { resumo, por_categoria = [], por_conta = [] } = dados;
    if (!resumo) return null;
    const saldo = (resumo.receitas || 0) - (resumo.despesas || 0);
    const despesasCat = por_categoria.filter(c => c.tipo === 'despesa');
    const receitasCat = por_categoria.filter(c => c.tipo === 'receita');

    return (
      <>
        <div className="stat-grid">
          <div className="stat-card receita">
            <span className="stat-label">Receitas</span>
            <span className="stat-value">{fmtMoeda(resumo.receitas)}</span>
            <span className="text-sm text-muted">{resumo.qtd_receitas} lançamentos</span>
          </div>
          <div className="stat-card despesa">
            <span className="stat-label">Despesas</span>
            <span className="stat-value">{fmtMoeda(resumo.despesas)}</span>
            <span className="text-sm text-muted">{resumo.qtd_despesas} lançamentos</span>
          </div>
          <div className={`stat-card ${saldo >= 0 ? 'receita' : 'despesa'}`}>
            <span className="stat-label">Resultado</span>
            <span className="stat-value">{fmtMoeda(Math.abs(saldo))}</span>
            <span className="text-sm text-muted">{saldo >= 0 ? '✅ superávit' : '⚠️ déficit'}</span>
          </div>
          {resumo.despesas > 0 && (
            <div className="stat-card">
              <span className="stat-label">Taxa de poupança</span>
              <span className="stat-value" style={{ color: 'var(--purple)' }}>
                {resumo.receitas > 0 ? ((saldo / resumo.receitas) * 100).toFixed(1) : 0}%
              </span>
            </div>
          )}
        </div>

        <div className="grid-2">
          {/* Despesas por categoria */}
          {despesasCat.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">⬇️ Despesas por Categoria</span></div>
              <div className="card-body">
                {despesasCat.map((c, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div className="flex-between mb-1">
                      <span style={{ fontSize: 13 }}>{c.icone} {c.nome}</span>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{fmtMoeda(c.total)}</span>
                        {resumo.despesas > 0 && <span className="text-muted text-sm"> ({((c.total / resumo.despesas) * 100).toFixed(1)}%)</span>}
                      </div>
                    </div>
                    <div className="progress-bar-wrap">
                      <div className="progress-bar-fill" style={{ width: `${Math.min(100, (c.total / resumo.despesas) * 100)}%`, background: c.cor || '#dc2626' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Receitas por categoria */}
          {receitasCat.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">⬆️ Receitas por Categoria</span></div>
              <div className="card-body">
                {receitasCat.map((c, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div className="flex-between mb-1">
                      <span style={{ fontSize: 13 }}>{c.icone} {c.nome}</span>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{fmtMoeda(c.total)}</span>
                    </div>
                    <div className="progress-bar-wrap">
                      <div className="progress-bar-fill" style={{ width: `${Math.min(100, resumo.receitas > 0 ? (c.total / resumo.receitas) * 100 : 0)}%`, background: c.cor || '#16a34a' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Por conta */}
        {por_conta.length > 0 && (
          <div className="card mt-3">
            <div className="card-header"><span className="card-title">🏦 Por Conta Bancária</span></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Conta</th><th>Entradas</th><th>Saídas</th><th>Resultado</th></tr></thead>
                <tbody>
                  {por_conta.map((c, i) => {
                    const res = c.entradas - c.saidas;
                    return (
                      <tr key={i}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="color-dot" style={{ background: c.cor }} />
                            <span style={{ fontSize: 13 }}>{c.icone} {c.nome}</span>
                          </div>
                        </td>
                        <td className="value-positive">{fmtMoeda(c.entradas)}</td>
                        <td className="value-negative">{fmtMoeda(c.saidas)}</td>
                        <td className={res >= 0 ? 'value-positive' : 'value-negative'}>{fmtMoeda(res)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderAnual = () => {
    if (!dados) return null;
    const { por_mes = [], totais_ano, top_categorias = [] } = dados;
    if (!totais_ano || !Array.isArray(por_mes)) return null;
    const saldoAno = (totais_ano.total_receitas || 0) - (totais_ano.total_despesas || 0);

    return (
      <>
        <div className="stat-grid">
          <div className="stat-card receita">
            <span className="stat-label">Total Receitas {ano}</span>
            <span className="stat-value">{fmtMoeda(totais_ano.total_receitas)}</span>
          </div>
          <div className="stat-card despesa">
            <span className="stat-label">Total Despesas {ano}</span>
            <span className="stat-value">{fmtMoeda(totais_ano.total_despesas)}</span>
          </div>
          <div className={`stat-card ${saldoAno >= 0 ? 'receita' : 'despesa'}`}>
            <span className="stat-label">Resultado {ano}</span>
            <span className="stat-value">{fmtMoeda(Math.abs(saldoAno))}</span>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header"><span className="card-title">📊 Receitas vs Despesas por Mês</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Mês</th><th>Receitas</th><th>Despesas</th><th>Resultado</th></tr></thead>
              <tbody>
                {por_mes.map(m => {
                  const res = m.receitas - m.despesas;
                  return (
                    <tr key={m.mes}>
                      <td style={{ fontWeight: 500 }}>{MESES[(m.num_mes || 1) - 1]}/{ano}</td>
                      <td className="value-positive">{fmtMoeda(m.receitas)}</td>
                      <td className="value-negative">{fmtMoeda(m.despesas)}</td>
                      <td className={res >= 0 ? 'value-positive' : 'value-negative'}>{fmtMoeda(res)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {top_categorias.length > 0 && (
          <div className="card">
            <div className="card-header"><span className="card-title">🏷️ Top 10 Categorias de Despesa</span></div>
            <div className="card-body">
              {top_categorias.map((c, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div className="flex-between mb-1">
                    <span style={{ fontSize: 13 }}>{i + 1}. {c.icone} {c.nome}</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{fmtMoeda(c.total)}</span>
                  </div>
                  <div className="progress-bar-wrap">
                    <div className="progress-bar-fill" style={{ width: `${Math.min(100, (c.total / top_categorias[0].total) * 100)}%`, background: c.cor || '#dc2626' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderPatrimonio = () => {
    if (!dados) return null;
    const { contas = [], cartoes = [], metas = [], total_ativos = 0, total_dividas = 0, patrimonio_liquido = 0 } = dados;
    if (!Array.isArray(contas)) return <div className="empty-state"><p>Sem dados de patrimônio disponíveis.</p></div>;

    return (
      <>
        <div className="stat-grid">
          <div className="stat-card saldo">
            <span className="stat-label">Total em Ativos</span>
            <span className="stat-value">{fmtMoeda(total_ativos)}</span>
          </div>
          <div className="stat-card despesa">
            <span className="stat-label">Total em Dívidas</span>
            <span className="stat-value">{fmtMoeda(total_dividas)}</span>
          </div>
          <div className={`stat-card ${patrimonio_liquido >= 0 ? 'receita' : 'despesa'}`}>
            <span className="stat-label">Patrimônio Líquido</span>
            <span className="stat-value">{fmtMoeda(Math.abs(patrimonio_liquido))}</span>
            {patrimonio_liquido < 0 && <span className="text-sm text-muted">negativo</span>}
          </div>
        </div>

        <div className="grid-2">
          <div className="card">
            <div className="card-header"><span className="card-title">🏦 Saldo por Conta</span></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Conta</th><th>Tipo</th><th>Saldo</th></tr></thead>
                <tbody>
                  {contas.map((c, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{c.nome}</td>
                      <td><span className="badge badge-primary" style={{ fontSize: 10 }}>{c.tipo}</span></td>
                      <td className={parseFloat(c.saldo_atual) >= 0 ? 'value-positive' : 'value-negative'}>{fmtMoeda(c.saldo_atual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">💳 Cartões de Crédito</span></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Cartão</th><th>Limite</th><th>Utilizado</th><th>Disponível</th></tr></thead>
                <tbody>
                  {cartoes.map((c, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{c.nome}</td>
                      <td style={{ fontSize: 13 }}>{fmtMoeda(c.limite)}</td>
                      <td className="value-negative">{fmtMoeda(c.utilizado)}</td>
                      <td className="value-positive">{fmtMoeda(c.limite_disponivel)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {metas.length > 0 && (
          <div className="card mt-3">
            <div className="card-header"><span className="card-title">🎯 Progresso das Metas</span></div>
            <div className="card-body">
              {metas.map((m, i) => {
                const pct = Math.min(100, (m.valor_atual / m.valor_objetivo) * 100);
                return (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div className="flex-between mb-1">
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{m.icone} {m.nome}</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{fmtMoeda(m.valor_atual)} / {fmtMoeda(m.valor_objetivo)}</span>
                    </div>
                    <div className="progress-bar-wrap">
                      <div className="progress-bar-fill" style={{ width: `${pct}%`, background: m.cor || '#16a34a' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Layout title="Relatórios">
      <div className="flex-between mb-3">
        <div className="tabs" style={{ borderBottom: 'none', marginBottom: 0 }}>
          {[['mensal', '📅 Mensal'], ['anual', '📆 Anual'], ['patrimonio', '💎 Patrimônio']].map(([k, v]) => (
            <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{v}</button>
          ))}
        </div>
        <div className="flex-gap">
          {tab === 'mensal' && <input type="month" value={mes} onChange={e => setMes(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--gray-200)', borderRadius: 7, fontSize: 13 }} />}
          {tab === 'anual' && (
            <input type="number" value={ano} onChange={e => setAno(e.target.value)} min="2000" max="2100"
              style={{ padding: '7px 10px', border: '1.5px solid var(--gray-200)', borderRadius: 7, fontSize: 13, width: 100 }} />
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-wrap"><div className="spinner" /></div>
      ) : (
        <>
          {tab === 'mensal' && renderMensal()}
          {tab === 'anual' && renderAnual()}
          {tab === 'patrimonio' && renderPatrimonio()}
        </>
      )}
    </Layout>
  );
}
